import { useAuth } from "../../context/AuthContext";
import { useEffect, useMemo, useRef, useState } from "react";
import { BarChart3, PhoneIncoming, PhoneMissed, PhoneOutgoing, Play } from "lucide-react";
import { Badge } from "../../components/ui/badge";
import { CopyButton } from "../../components/ui/copy-button";
import { formatDuration, formatTimestamp } from "../../lib/time";
import { cn } from "../../lib/utils";
import {
  SENTIMENT_META, STATUS_META, type CallRecord,
} from "./types";
import {
  CALL_COLUMNS,
  DEFAULT_COLUMN_WIDTHS,
  MIN_COLUMN_WIDTHS,
  type CallColumnId,
  type ColumnId,
} from "./column-config";

interface CallsTableProps {
  calls: CallRecord[];
  retryMode: boolean;
  selectedIds: Set<string>;
  onToggleSelect: (id: string) => void;
  onToggleSelectAll: () => void;
  allSelected: boolean;
  onOpenRecording: (call: CallRecord) => void;
  onOpenPca: (call: CallRecord) => void;
  onRowClick: (call: CallRecord) => void;
  isFiltered: boolean;
  hasMore: boolean;
  loading: boolean;
  onLoadMore: () => void;
  onSeed: () => void;
  availabilityNote?: string | null;
  visible: ReadonlySet<CallColumnId>;
  scrollRef?: React.RefObject<HTMLDivElement | null>;
  columnWidths?: Record<ColumnId, number>;
  onColumnWidthsChange?: (widths: Record<ColumnId, number>) => void;
}

const TH = "px-4 py-3 text-left font-mono text-xs font-semibold uppercase tracking-wider";
const TD = "px-4 py-3.5 align-middle";

export function CallsTable(p: CallsTableProps) {
  const { user } = useAuth() as any;
  const sentinelRef = useRef<HTMLDivElement>(null);
  const { hasMore, loading, onLoadMore } = p;
  const show = (id: CallColumnId) => p.visible.has(id);

  const [widths, setWidths] = useState<Record<ColumnId, number>>(() => ({
    ...DEFAULT_COLUMN_WIDTHS,
    ...(p.columnWidths || {}),
  }));
  const [resizingCol, setResizingCol] = useState<ColumnId | null>(null);

  // Sync if parent updates columnWidths (e.g. Reset Widths button in toolbar)
  useEffect(() => {
    if (p.columnWidths) {
      setWidths({ ...DEFAULT_COLUMN_WIDTHS, ...p.columnWidths });
    }
  }, [p.columnWidths]);

  useEffect(() => {
    const el = sentinelRef.current;
    if (!el || !hasMore || loading) return;
    const obs = new IntersectionObserver(
      (entries) => entries[0].isIntersecting && onLoadMore(),
      { rootMargin: "200px" },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [hasMore, loading, onLoadMore]);

  const handleResizeStart = (colId: ColumnId, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    const startX = e.clientX;
    const initialWidth = widths[colId] ?? DEFAULT_COLUMN_WIDTHS[colId];
    const minWidth = MIN_COLUMN_WIDTHS[colId] ?? 60;

    setResizingCol(colId);

    const prevCursor = document.body.style.cursor;
    const prevUserSelect = document.body.style.userSelect;
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";

    let latestWidth = initialWidth;

    const onMouseMove = (moveEvent: MouseEvent) => {
      moveEvent.preventDefault();
      const deltaX = moveEvent.clientX - startX;
      latestWidth = Math.max(minWidth, Math.round(initialWidth + deltaX));
      setWidths((prev) => ({
        ...prev,
        [colId]: latestWidth,
      }));
    };

    const onMouseUp = () => {
      setResizingCol(null);
      document.body.style.cursor = prevCursor;
      document.body.style.userSelect = prevUserSelect;
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);

      if (p.onColumnWidthsChange) {
        setWidths((current) => {
          const final = { ...current, [colId]: latestWidth };
          p.onColumnWidthsChange?.(final);
          return final;
        });
      }
    };

    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp);
  };

  const handleDoubleClick = (colId: ColumnId, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const defaultW = DEFAULT_COLUMN_WIDTHS[colId];
    setWidths((prev) => {
      const next = { ...prev, [colId]: defaultW };
      p.onColumnWidthsChange?.(next);
      return next;
    });
  };

  const totalTableWidth = useMemo(() => {
    let sum = p.retryMode ? 44 : 0;
    sum += widths.contactId ?? DEFAULT_COLUMN_WIDTHS.contactId;
    for (const col of CALL_COLUMNS) {
      if (p.visible.has(col.id)) {
        sum += widths[col.id] ?? DEFAULT_COLUMN_WIDTHS[col.id];
      }
    }
    return sum;
  }, [p.retryMode, p.visible, widths]);

  const renderHeader = (colId: ColumnId, label: string) => {
    const isResizing = resizingCol === colId;
    return (
      <th
        key={colId}
        style={{ width: widths[colId] }}
        className={cn(TH, "relative group select-none pr-3")}
      >
        <div className="flex items-center justify-between gap-1 overflow-hidden">
          <span className="truncate">{label}</span>
        </div>
        <div
          role="separator"
          aria-orientation="vertical"
          aria-label={`Resize ${label} column`}
          className={cn(
            "absolute -right-1 top-0 h-full w-2.5 cursor-col-resize select-none z-10 flex items-center justify-center transition-colors",
            "hover:bg-brand/10 active:bg-brand/20",
            isResizing && "bg-brand/20"
          )}
          onMouseDown={(e) => handleResizeStart(colId, e)}
          onDoubleClick={(e) => handleDoubleClick(colId, e)}
          title="Drag to resize, double-click to normalize"
        >
          <div
            className={cn(
              "h-3.5 w-[2px] rounded-full bg-line/80 transition-all",
              "group-hover:h-5 group-hover:bg-brand/60",
              isResizing && "h-full bg-brand"
            )}
          />
        </div>
      </th>
    );
  };

  return (
    <div className="overflow-hidden rounded-xl shadow-card dark:shadow-card-dark bg-paper-card dark:bg-[#292524]">
      <div ref={p.scrollRef} className="overflow-x-auto">
        <table
          className="w-full text-sm"
          style={{ tableLayout: "fixed", minWidth: `${totalTableWidth}px` }}
        >
          <colgroup>
            {p.retryMode && <col style={{ width: 44 }} />}
            <col style={{ width: widths.contactId }} />
            {show("direction") && <col style={{ width: widths.direction }} />}
            {show("phone") && <col style={{ width: widths.phone }} />}
            {show("schedule") && <col style={{ width: widths.schedule }} />}
            {show("agent") && <col style={{ width: widths.agent }} />}
            {show("version") && <col style={{ width: widths.version }} />}
            {show("voice") && <col style={{ width: widths.voice }} />}
            {show("duration") && <col style={{ width: widths.duration }} />}
            {show("timestamp") && <col style={{ width: widths.timestamp }} />}
            {show("status") && <col style={{ width: widths.status }} />}
            {show("rec") && <col style={{ width: widths.rec }} />}
            {show("pca") && <col style={{ width: widths.pca }} />}
            {show("hungBy") && <col style={{ width: widths.hungBy }} />}
            {show("campaign") && <col style={{ width: widths.campaign }} />}
            {show("sentiment") && <col style={{ width: widths.sentiment }} />}
            {show("executionId") && <col style={{ width: widths.executionId }} />}
            <col />
          </colgroup>
          <thead className="bg-soft text-ink-3">
            <tr>
              {p.retryMode && (
                <th style={{ width: 44 }} className={cn(TH, "w-11 px-3 text-center")}>
                  <input
                    type="checkbox"
                    checked={p.allSelected}
                    onChange={p.onToggleSelectAll}
                    aria-label="Select all retryable calls"
                    className="h-4 w-4 rounded border-line accent-brand"
                  />
                </th>
              )}
              {renderHeader("contactId", "Contact ID")}
              {show("direction") && renderHeader("direction", "Direction")}
              {show("phone") && renderHeader("phone", "Phone")}
              {show("schedule") && renderHeader("schedule", "Schedule ID")}
              {show("agent") && renderHeader("agent", "Agent")}
              {show("version") && renderHeader("version", "Version")}
              {show("voice") && renderHeader("voice", "Voice")}
              {show("duration") && renderHeader("duration", "Duration")}
              {show("timestamp") && renderHeader("timestamp", "Timestamp")}
              {show("status") && renderHeader("status", "Status")}
              {show("rec") && renderHeader("rec", "Rec")}
              {show("pca") && renderHeader("pca", "Analysis")}
              {show("hungBy") && renderHeader("hungBy", "Hung By")}
              {show("campaign") && renderHeader("campaign", "Campaign")}
              {show("sentiment") && renderHeader("sentiment", "Sentiment")}
              {show("executionId") && renderHeader("executionId", "Execution ID")}
              <th className="p-0 border-0" />
            </tr>
          </thead>
          <tbody>
            {p.calls.map((c) => {
              const st = STATUS_META[c.status] || {
                label: c.status ? String(c.status).charAt(0).toUpperCase() + String(c.status).slice(1) : "—",
                tone: "neutral" as const,
              };
              const se = SENTIMENT_META[c.sentiment] || {
                label: c.sentiment ? String(c.sentiment).charAt(0).toUpperCase() + String(c.sentiment).slice(1) : "—",
                tone: "neutral" as const,
              };
              return (
                <tr
                  key={c.executionId}
                  tabIndex={0}
                  onClick={() => p.onRowClick(c)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") p.onRowClick(c);
                  }}
                  className="cursor-pointer border-t border-line transition-colors hover:bg-soft/50 focus:bg-soft/50 focus:outline-none"
                >
                  {p.retryMode && (
                    <td className={cn(TD, "w-11 px-3 text-center")}>
                      {c.retryable && c.isScheduledCall ? (
                        <input
                          type="checkbox"
                          checked={p.selectedIds.has(c.executionId)}
                          onChange={() => p.onToggleSelect(c.executionId)}
                          onClick={(event) => event.stopPropagation()}
                          aria-label={`Select ${c.executionId} for retry`}
                          className="h-4 w-4 rounded border-line accent-brand"
                        />
                      ) : (
                        <span className="text-ink-3">—</span>
                      )}
                    </td>
                  )}
                  <td className={cn(TD, "font-mono text-ink")}>
                    <span className="flex items-center gap-1 min-w-0">
                      <span className="truncate" title={c.contactId}>
                        {c.contactId}
                      </span>
                      <CopyButton value={c.contactId} className="shrink-0" />
                    </span>
                  </td>
                  {show("direction") && (
                    <td className={TD}>
                      {c.direction === "inbound" ? (
                        <span
                          className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20"
                          title={c.destinationDid ? `Inbound to ${c.destinationDid}` : "Inbound call"}
                        >
                          <PhoneIncoming className="h-3 w-3" />
                          Inbound
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium bg-slate-500/10 text-slate-600 dark:text-slate-400 border border-slate-500/20">
                          <PhoneOutgoing className="h-3 w-3" />
                          Outbound
                        </span>
                      )}
                    </td>
                  )}
                  {show("phone") && (
                    <td className={cn(TD, "font-mono text-ink-2")}>
                      {c.phone ? (
                        <span className="flex items-center gap-1 min-w-0">
                          <span className="truncate">{c.phone}</span>
                          <CopyButton value={c.phone} className="shrink-0" />
                        </span>
                      ) : (
                        <span className="text-ink-3">—</span>
                      )}
                    </td>
                  )}
                  {show("schedule") && (
                    <td className={cn(TD, "font-mono text-ink-2")}>
                      {c.scheduleId ? (
                        <span className="flex items-center gap-1 min-w-0">
                          <span className="truncate" title={c.scheduleId}>
                            {c.scheduleId}
                          </span>
                          <CopyButton value={c.scheduleId} className="shrink-0" />
                        </span>
                      ) : (
                        <span className="text-ink-3">—</span>
                      )}
                    </td>
                  )}
                  {show("agent") && <td className={cn(TD, "font-medium text-ink truncate")} title={c.agent}>{c.agent}</td>}
                  {show("version") && <td className={cn(TD, "font-mono text-ink-2 truncate")}>{c.version}</td>}
                  {show("voice") && <td className={cn(TD, "text-ink-2 truncate")} title={c.voice}>{c.voice}</td>}
                  {show("duration") && <td className={cn(TD, "font-mono text-ink whitespace-nowrap")}>{formatDuration(c.durationSec)}</td>}
                  {show("timestamp") && <td className={cn(TD, "text-ink-2 whitespace-nowrap")}>{formatTimestamp(c.createdAt, user?.timezone)}</td>}
                  {show("status") && <td className={TD}><Badge tone={st.tone}>{st.label}</Badge></td>}
                  {show("rec") && (
                    <td className={TD}>
                      <button
                        onClick={(event) => {
                          event.stopPropagation();
                          p.onOpenRecording(c);
                        }}
                        disabled={c.status === "failed" || (!c.transcript.length && !c.recordingUrl)}
                        className={cn(
                          "flex items-center gap-1 rounded-lg px-2 py-1 text-xs font-semibold transition-colors",
                          c.status !== "failed" && (c.transcript.length || c.recordingUrl)
                            ? "bg-soft text-ink hover:bg-line"
                            : "cursor-not-allowed text-ink-3 opacity-50",
                        )}
                      >
                        <Play className="h-3 w-3" /> Play
                      </button>
                    </td>
                  )}
                  {show("pca") && (
                    <td className={TD}>
                      <button
                        onClick={(event) => {
                          event.stopPropagation();
                          p.onOpenPca(c);
                        }}
                        className="flex items-center gap-1 rounded-lg bg-soft px-2 py-1 text-xs font-semibold text-ink transition-colors hover:bg-line"
                      >
                        <BarChart3 className="h-3 w-3" /> Analysis
                      </button>
                    </td>
                  )}
                  {show("hungBy") && <td className={cn(TD, "capitalize text-ink-2 truncate")}>{c.hungBy}</td>}
                  {show("campaign") && <td className={cn(TD, "text-ink truncate")} title={c.campaign}>{c.campaign}</td>}
                  {show("sentiment") && <td className={TD}><Badge tone={se.tone}>{se.label}</Badge></td>}
                  {show("executionId") && (
                    <td className={cn(TD, "font-mono text-xs text-ink-3")}>
                      <span className="flex items-center gap-1 min-w-0">
                        <span className="truncate" title={c.executionId}>
                          {c.executionId}
                        </span>
                        <CopyButton value={c.executionId} className="shrink-0" />
                      </span>
                    </td>
                  )}
                  <td className="p-0 border-0" />
                </tr>
              );
            })}
          </tbody>
        </table>

        {p.calls.length === 0 && (
          <div className="p-16 text-center">
            <PhoneMissed className="mx-auto h-10 w-10 text-line" />
            <p className="mt-4 text-lg font-bold text-ink">
              {p.retryMode ? "No scheduled calls found for retry" : "No calls found"}
            </p>
            
            {!p.retryMode && (
              <button
                onClick={p.onSeed}
                className="mt-4 rounded-lg bg-brand px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-brand-hover"
              >
                Seed Demo Data
              </button>
            )}
          </div>
        )}
      </div>

      {p.hasMore && (
        <div ref={sentinelRef} className="flex justify-center py-3">
          {p.loading && <span className="font-mono text-xs text-ink-3">Loading more calls…</span>}
        </div>
      )}
    </div>
  );
}
